/**
 * Copyright (c) 2010-2023 California CAD Solutions, Inc. Licensed subject
 * to an End-User License Agreement included in a separate file named EULA.txt
 * that is distributed with this file.
 */
import { Subject } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { SearchDisplayService } from '../../../search/services/search-display.service';
import { MapCommandMenuItem } from '../../types';
import { MapCommand } from '../map-command.class';

export class SearchCommand extends MapCommand {
    private searchDisplay: SearchDisplayService;

    invoke() {
        this.searchDisplay.previousSearchAvailable$.pipe(take(1)).subscribe((hasResults) => {
            if (!hasResults) {
                this.newSearch();
            } else {
                this.preventClose$.next(true);
            }
        });
    }

    protected onCommandInit() {
        super.onCommandInit();
        this.searchDisplay = this.deps['searchDisplayService'];

        this.menuItems$ = this.searchDisplay.previousSearchAvailable$.pipe(
            map((hasPrevious: boolean): MapCommandMenuItem[] =>
                hasPrevious
                    ? [
                          {
                              label: 'Restore Last Search',
                              action: () => this.previousSearch(),
                          },
                          {
                              label: 'New Search',
                              action: () => this.newSearch(),
                          },
                          {
                              label: 'Clear Result Features',
                              action: () => this.clearResults(),
                          },
                      ]
                    : []
            )
        );
    }

    private newSearch() {
        this.preventClose$.next(false);
        const closed$ = new Subject<void>();
        this.searchDisplay.showChooseDialog(closed$);

        closed$.pipe(take(1)).subscribe(() => this.deactivate$.next());
    }

    private previousSearch() {
        this.preventClose$.next(false);
        const closed$ = new Subject<void>();
        this.searchDisplay.runPreviousSearch(closed$);

        closed$.pipe(take(1)).subscribe(() => this.deactivate$.next());
    }

    private clearResults() {
        this.preventClose$.next(false);
        this.searchDisplay.clearSearchResults();
        this.deactivate$.next();
    }
}
